import axiosInstance from '../../../config/axiosInstance';
import type {
  CreateProjectVitalFinanceAccountPayload,
  CreateProjectVitalFinanceMovementPayload,
  CreateProjectVitalFinancePlanPayload,
  CreateProjectVitalIssuePayload,
  CreateProjectVitalTaskPayload,
  ProjectVitalFinanceAccount,
  ProjectVitalFinanceForecast,
  ProjectVitalFinanceHistoryPoint,
  ProjectVitalFinanceMovement,
  ProjectVitalFinancePlan,
  ProjectVitalFinanceOverview,
  ProjectVitalFinanceViewScope,
  ProjectVitalIssue,
  ProjectVitalTask,
  UpdateProjectVitalFinanceAccountPayload,
  UpdateProjectVitalFinanceMovementPayload,
  UpdateProjectVitalFinancePlanPayload,
  UpdateProjectVitalIssuePayload,
  UpdateProjectVitalTaskPayload,
} from '../types/projectVital';

export type ProjectVitalAccessContext = {
  user_id: string;
  has_access: boolean;
};

export async function getProjectVitalAccessContext(): Promise<ProjectVitalAccessContext> {
  const { data } = await axiosInstance.get('/project-vital/context');
  return data as ProjectVitalAccessContext;
}

export async function listProjectVitalTasks(): Promise<ProjectVitalTask[]> {
  const { data } = await axiosInstance.get('/project-vital/tasks');
  return data as ProjectVitalTask[];
}

export async function createProjectVitalTask(
  payload: CreateProjectVitalTaskPayload,
): Promise<ProjectVitalTask> {
  const { data } = await axiosInstance.post('/project-vital/tasks', payload);
  return data as ProjectVitalTask;
}

export async function updateProjectVitalTask(
  taskId: string,
  payload: UpdateProjectVitalTaskPayload,
): Promise<ProjectVitalTask> {
  const { data } = await axiosInstance.put(
    `/project-vital/tasks/${taskId}`,
    payload,
  );
  return data as ProjectVitalTask;
}

export async function listProjectVitalIssues(): Promise<ProjectVitalIssue[]> {
  const { data } = await axiosInstance.get('/project-vital/issues');
  return data as ProjectVitalIssue[];
}

export async function createProjectVitalIssue(
  payload: CreateProjectVitalIssuePayload,
): Promise<ProjectVitalIssue> {
  const { data } = await axiosInstance.post('/project-vital/issues', payload);
  return data as ProjectVitalIssue;
}

export async function updateProjectVitalIssue(
  issueId: string,
  payload: UpdateProjectVitalIssuePayload,
): Promise<ProjectVitalIssue> {
  const { data } = await axiosInstance.put(
    `/project-vital/issues/${issueId}`,
    payload,
  );
  return data as ProjectVitalIssue;
}

export async function getProjectVitalFinanceOverview(
  month: string,
  scope: ProjectVitalFinanceViewScope = 'global',
): Promise<ProjectVitalFinanceOverview> {
  const { data } = await axiosInstance.get('/project-vital/finance/overview', {
    params: { month, scope },
  });
  return data as ProjectVitalFinanceOverview;
}

export async function getProjectVitalFinanceHistory(
  through: string,
  months = 6,
  scope: ProjectVitalFinanceViewScope = 'global',
): Promise<ProjectVitalFinanceHistoryPoint[]> {
  const { data } = await axiosInstance.get('/project-vital/finance/history', {
    params: {
      through,
      months,
      scope,
    },
  });

  return data as ProjectVitalFinanceHistoryPoint[];
}

export async function createProjectVitalFinanceAccount(
  payload: CreateProjectVitalFinanceAccountPayload,
): Promise<ProjectVitalFinanceAccount> {
  const { data } = await axiosInstance.post(
    '/project-vital/finance/accounts',
    payload,
  );
  return data as ProjectVitalFinanceAccount;
}

export async function updateProjectVitalFinanceAccount(
  accountId: string,
  payload: UpdateProjectVitalFinanceAccountPayload,
): Promise<ProjectVitalFinanceAccount> {
  const { data } = await axiosInstance.put(
    `/project-vital/finance/accounts/${accountId}`,
    payload,
  );
  return data as ProjectVitalFinanceAccount;
}

export async function setProjectVitalFinanceAccountActive(
  accountId: string,
  isActive: boolean,
): Promise<ProjectVitalFinanceAccount> {
  const { data } = await axiosInstance.patch(
    `/project-vital/finance/accounts/${accountId}/status`,
    {
      is_active: isActive,
    },
  );

  return data as ProjectVitalFinanceAccount;
}

export async function createProjectVitalFinanceMovement(
  payload: CreateProjectVitalFinanceMovementPayload,
): Promise<ProjectVitalFinanceMovement> {
  const { data } = await axiosInstance.post(
    '/project-vital/finance/movements',
    payload,
  );
  return data as ProjectVitalFinanceMovement;
}

export async function updateProjectVitalFinanceMovement(
  movementId: string,
  payload: UpdateProjectVitalFinanceMovementPayload,
): Promise<ProjectVitalFinanceMovement> {
  const { data } = await axiosInstance.put(
    `/project-vital/finance/movements/${movementId}`,
    payload,
  );
  return data as ProjectVitalFinanceMovement;
}

export async function deleteProjectVitalFinanceMovement(
  movementId: string,
): Promise<void> {
  await axiosInstance.delete(
    `/project-vital/finance/movements/${movementId}`,
  );
}

export async function listProjectVitalFinancePlans(
  scope: ProjectVitalFinanceViewScope = 'global',
): Promise<ProjectVitalFinancePlan[]> {
  const { data } = await axiosInstance.get(
    '/project-vital/finance/plans',
    { params: { scope } },
  );

  return data as ProjectVitalFinancePlan[];
}

export async function createProjectVitalFinancePlan(
  payload: CreateProjectVitalFinancePlanPayload,
): Promise<ProjectVitalFinancePlan> {
  const { data } = await axiosInstance.post(
    '/project-vital/finance/plans',
    payload,
  );

  return data as ProjectVitalFinancePlan;
}

export async function updateProjectVitalFinancePlan(
  planId: string,
  payload: UpdateProjectVitalFinancePlanPayload,
): Promise<ProjectVitalFinancePlan> {
  const { data } = await axiosInstance.put(
    `/project-vital/finance/plans/${planId}`,
    payload,
  );

  return data as ProjectVitalFinancePlan;
}

export async function setProjectVitalFinancePlanActive(
  planId: string,
  isActive: boolean,
): Promise<ProjectVitalFinancePlan> {
  const { data } = await axiosInstance.patch(
    `/project-vital/finance/plans/${planId}/status`,
    {
      is_active: isActive,
    },
  );

  return data as ProjectVitalFinancePlan;
}

export async function getProjectVitalFinanceForecast(
  days = 60,
  scope: ProjectVitalFinanceViewScope = 'global',
): Promise<ProjectVitalFinanceForecast> {
  const { data } = await axiosInstance.get(
    '/project-vital/finance/forecast',
    {
      params: { days, scope },
    },
  );

  return data as ProjectVitalFinanceForecast;
}

export async function registerProjectVitalFinancePlanOccurrence(
  planId: string,
  occurrenceDate: string,
): Promise<ProjectVitalFinanceMovement> {
  const { data } = await axiosInstance.post(
    `/project-vital/finance/plans/${planId}/occurrences/${occurrenceDate}/register`,
  );

  return data as ProjectVitalFinanceMovement;
}
